/**
 * Money and time formatting.
 *
 * Amounts arrive as whole kopecks and are formatted without ever becoming a
 * float: the rouble and kopeck parts are split with integer arithmetic.
 */

const KOPECKS_PER_RUBLE = 100;
const GROUPING = new Intl.NumberFormat('ru-RU');
const DATE_TIME = new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium', timeStyle: 'short' });
const FULL_DATE_TIME = new Intl.DateTimeFormat('ru-RU', { dateStyle: 'full', timeStyle: 'medium' });
const DATE = new Intl.DateTimeFormat('ru-RU', { dateStyle: 'long' });

/** `150075` → `1 500,75 ₽`. A sign is added only when `withSign` is set. */
export function formatMoney(kopecks: number, options: { withSign?: boolean } = {}): string {
  const absolute = Math.abs(kopecks);
  const rubles = GROUPING.format(Math.trunc(absolute / KOPECKS_PER_RUBLE));
  const remainder = String(absolute % KOPECKS_PER_RUBLE).padStart(2, '0');

  return `${sign(kopecks, options.withSign ?? false)}${rubles},${remainder} ₽`;
}

function sign(kopecks: number, withSign: boolean): string {
  if (kopecks < 0) {
    return '−';
  }
  return withSign && kopecks > 0 ? '+' : '';
}

/** `18500000` → `18 500 000 коп.`, the exact value the API and MCP speak in. */
export function formatKopecks(kopecks: number): string {
  return `${GROUPING.format(kopecks)} коп.`;
}

export function formatDateTime(iso: string): string {
  return DATE_TIME.format(new Date(iso));
}

export function formatFullDateTime(iso: string): string {
  return FULL_DATE_TIME.format(new Date(iso));
}

/**
 * `2026-09-01` → `1 сентября 2026 г.`
 *
 * The parts are read out of the string instead of being parsed by `Date`: a
 * bare date is a moment in UTC to it, which west of Greenwich formats as the
 * day before. A payment falls due on a day, not at a moment.
 */
export function formatDate(date: string): string {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const day = Number(date.slice(8, 10));

  return DATE.format(new Date(year, month - 1, day));
}

/** Today in the timezone of the browser, as `YYYY-MM-DD`: due dates compare as strings. */
export function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}
